/**
 * @file auth-api.ts
 * @description Thin typed wrappers around the /auth/* endpoints. Each
 *   function takes a plain DTO and returns the parsed response or throws
 *   ApiError. UI code should depend on these rather than calling `api`
 *   directly — keeps endpoint paths in one place.
 * @module Lib
 * @security Access token is set on the in-memory store via setAccessToken;
 *   the refresh-token cookie is set by the server, never read by JS.
 */

import { api, setAccessToken } from './api';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  invitation_token: string;
  email: string;
  password: string;
  locale: 'he' | 'en';
}

export interface TokenResponse {
  access_token: string;
  token_type: 'Bearer';
}

export async function login(payload: LoginPayload): Promise<TokenResponse> {
  const res = await api<TokenResponse>('/auth/login', { method: 'POST', json: payload });
  setAccessToken(res.access_token);
  return res;
}

export async function registerFromInvitation(payload: RegisterPayload): Promise<TokenResponse> {
  const res = await api<TokenResponse>('/auth/register', { method: 'POST', json: payload });
  setAccessToken(res.access_token);
  return res;
}

export async function logout(): Promise<void> {
  await api<void>('/auth/logout', { method: 'POST' });
  setAccessToken(null);
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api<void>('/me/change-password', {
    method: 'POST',
    json: { current_password: currentPassword, new_password: newPassword },
  });
  // Server revoked all refresh tokens; clear local access token so the
  // next API call goes through fresh /auth/login.
  setAccessToken(null);
}

export async function requestPasswordReset(email: string): Promise<void> {
  await api<void>('/auth/forgot-password', { method: 'POST', json: { email } });
}

export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  await api<void>('/auth/reset-password', {
    method: 'POST',
    json: { token, new_password: newPassword },
  });
}
